import { actions } from "./action";

// Transferable skills live in their own container, so every list holding a
// player (players, allPlayers and the currently viewed player) has to be
// updated together — updating only one left the others holding the stale copy
// that later overwrote the fresh one.
const applyTransferableSkills = (state, userId, transferableSkills) => {
  const apply = (player) =>
    player && player.id === userId ? { ...player, transferableSkills } : player;

  return {
    ...state,
    players: state.players.map(apply),
    allPlayers: state.allPlayers.map(apply),
    player: apply(state.player),
  };
};

const skillsFromResource = (resource) => ({
  isActive: resource?.isActive === true,
  transferableSkills: resource?.transferableSkills || [],
});

const initialState = {
  player: null,
  players: [],
  club: [],
  allPlayers: [],
  playerStatus: [],
  playerCategory: [],
  existPlayers: [],
  errors: [],
  newPlayers: [],
  bulkUpload: { inProgress: false, done: 0, total: 0, stage: null },
  archivedPlayers: [],
  alumniPlayers: [],
  individualUsers: [],
  coursesOfUser: {
    userId: "",
    courses: [],
  },
  error: false,
};

const playerReducer = (state = initialState, action) => {
  switch (action.type) {
    case actions.GET_PLAYERS_SUCCESS:
      return {
        ...state,
        players: action.payload,
      };
    case actions.BULK_UPLOAD_PROGRESS:
      return {
        ...state,
        bulkUpload: action.payload,
      };
    case actions.CREATE_PLAYERS_SUCCESS:
      return {
        ...state,
        players: [...state.players, ...(action.payload.user || [])],
        existPlayers: action.payload.existUsers || [],
        errors: action.payload.errors || [],
        newPlayers: action.payload.user || [],
      };
    case actions.UPDATE_PLAYER_SUCCESS:
      // If the player was archived or moved to alumni, remove them from active lists
      if (action.payload?.isArchived || action.payload?.accountStatus === "alumni") {
        return {
          ...state,
          player: action.payload,
          allPlayers: state.allPlayers.filter((p) => p.id !== action.payload.id),
          players: state.players.filter((p) => p.id !== action.payload.id),
        };
      }

      // Merge rather than replace. The save response is the raw user document,
      // so it carries fewer — and differently shaped — fields than the read
      // endpoints that populated these lists. Replacing wholesale drops
      // anything the save does not echo back.
      //
      // dynamicProfileFields is the case that bites: reads return the org's
      // field definitions (templateId, options, title) merged with the user's
      // values, while a save returns only the stored values. Components filter
      // on templateId, so overwriting with the raw shape makes custom sections
      // vanish until the next full fetch.
      //
      // Recombine the two the same way the backend does when reading: keep the
      // definition, take value/userCustomTitle from the save, match on id.
      // Keeping the whole existing array instead would make an edited value
      // appear to revert, since these lists hold the last fetched values.
      const mergeDynamicProfileFields = (existingFields, incomingFields) => {
        if (!Array.isArray(existingFields)) return incomingFields;
        if (!Array.isArray(incomingFields)) return existingFields;

        const merged = existingFields.map((field) => {
          const saved = incomingFields.find((f) => f && f.id === field.id);
          if (!saved) return field;
          return {
            ...field,
            ...(saved.value !== undefined && { value: saved.value }),
            ...(saved.userCustomTitle !== undefined && {
              userCustomTitle: saved.userCustomTitle,
            }),
          };
        });

        // A field the user has just added exists only in the save response, so
        // mapping over the existing array alone drops it and the new section
        // vanishes until the next full fetch. Anything the save returned that
        // we have no definition for is appended as it stands.
        const known = new Set(existingFields.map((f) => f && f.id));
        incomingFields.forEach((field) => {
          if (field && !known.has(field.id)) merged.push(field);
        });

        return merged;
      };

      const mergePlayer = (existing, incoming) => {
        if (!existing) return incoming;
        const merged = { ...existing, ...incoming };
        merged.dynamicProfileFields = mergeDynamicProfileFields(
          existing.dynamicProfileFields,
          incoming.dynamicProfileFields
        );
        return merged;
      };

      const allPlayersIndex = state.allPlayers.findIndex(
        (player) => player.id === action.payload.id
      );
      const updatedAllPlayers = [...state.allPlayers];
      if (allPlayersIndex !== -1) {
        updatedAllPlayers[allPlayersIndex] = mergePlayer(
          state.allPlayers[allPlayersIndex],
          action.payload
        );
      }

      const playersIndex = state.players.findIndex(
        (player) => player.id === action.payload.id
      );
      const updatedPlayers = [...state.players];
      if (playersIndex !== -1) {
        updatedPlayers[playersIndex] = mergePlayer(
          state.players[playersIndex],
          action.payload
        );
      }

      return {
        ...state,
        player: mergePlayer(state.player, action.payload),
        allPlayers: updatedAllPlayers,
        players: updatedPlayers,
      };

    case actions.GET_CLUB_SUCCESS:
      return {
        ...state,
        club: action.payload,
      };

    case actions.CLEAR_ALL_PLAYERS:
      return { ...state, allPlayers: [] };

    case actions.GET_ALL_PLAYERS_SUCCESS:
      return {
        ...state,
        // allPlayers: [...action.payload],
        allPlayers: action.payload.filter((player) => {
          return !player?.isArchived;
        }),
        // archivedPlayers: action.payload.filter((player) => {
        //   return player?.isArchived;
        // }),
      };

    case actions.GET_ARCHIVED_PLAYERS_SUCCESS:
      return {
        ...state,
        // allPlayers: [...action.payload],
        archivedPlayers: [...action.payload],
      };

    case actions.GET_PLAYER_STATUS_SUCCESS:
      return {
        ...state,
        playerStatus: action.payload,
      };

    case actions.GET_PLAYER_CATEGORY_SUCCESS:
      return {
        ...state,
        playerCategory: action.payload,
      };

    case actions.REMOVE_PLAYER_SUCCESS:
      return {
        ...state,
        player: action.payload,
        allPlayers: state.allPlayers.filter((p) => p.id !== action.payload?.id),
        players: state.players.filter((p) => p.id !== action.payload?.id),
        archivedPlayers: [action.payload, ...state.archivedPlayers],
        alumniPlayers: state.alumniPlayers.filter((p) => p.id !== action.payload?.id),
      };

    case actions.UNREMOVE_PLAYER_SUCCESS:
      return {
        ...state,
        player: action.payload,
        allPlayers: [action.payload, ...state.allPlayers],
        // .sort((a, b) =>
        //   a?.name.localeCompare(b?.name)
        // ),
        archivedPlayers: state.archivedPlayers.filter((player) => {
          return player?.id !== action?.payload?.id;
        }),
      };

    case actions.DELETE_PLAYER_SUCCESS:
      return {
        ...state,
        // tutors: [...state.tutors, ...action.payload].sort((a, b) =>
        //   a.name.localeCompare(b.name)
        // ),
        // tutors: [resDataClubAdmin, ...sortDataAll],
        archivedPlayers: state.archivedPlayers.filter((player) => {
          return player?.id !== action?.payload;
        }),
      };
    case actions.ADD_TRANSFERABLE_SUCCESS:
      return applyTransferableSkills(
        state,
        action.payload?.userId,
        skillsFromResource(action.payload)
      );

    case actions.UPDATE_TRANSFERABLE_SUCCESS:
      return applyTransferableSkills(
        state,
        action.payload?.userId,
        action.payload?.resource
          ? skillsFromResource(action.payload.resource)
          : {
              isActive: action.payload?.isActive === true,
              transferableSkills:
                action.payload?.transferableSkills ||
                state.player?.transferableSkills?.transferableSkills ||
                [],
            }
      );

    case actions.GET_TRANSFERABLE_SUCCESS:
      return applyTransferableSkills(
        state,
        action.payload?.userId,
        skillsFromResource(action.payload?.resource)
      );

    case actions.DELETE_TRANSFERABLE_SUCCESS:
      return applyTransferableSkills(
        state,
        action.payload?.userId,
        skillsFromResource(action.payload?.resource)
      );

    case actions.GET_ALUMNI_PLAYERS_SUCCESS:
      return {
        ...state,
        alumniPlayers: action.payload,
      };

    case actions.GET_INDIVIDUAL_USERS_SUCCESS:
      return {
        ...state,
        individualUsers: action.payload,
      };

    case actions.MOVE_TO_ALUMNI_SUCCESS:
      // payload is array of ids — remove them from the active list
      return {
        ...state,
        allPlayers: state.allPlayers.filter((p) => !action.payload.includes(p.id)),
        players: state.players.filter((p) => !action.payload.includes(p.id)),
      };

    case actions.RESTORE_ALUMNI_SUCCESS:
      return {
        ...state,
        alumniPlayers: state.alumniPlayers.filter((p) => p.id !== action.payload.id),
        allPlayers: action.payload.player ? [action.payload.player, ...state.allPlayers] : state.allPlayers,
      };

    case actions.GET_PLAYER_COURSES_SUCCESS:
      return {
        ...state,
        coursesOfUser: action.payload,
      };

    case actions.CATCH_ERROR:
      return {
        ...state,
        error: action.payload,
      };
    default:
      return state;
  }
};

export default playerReducer;
